import { ArrowUpRight, Copy } from 'lucide-react'
import { Fragment } from 'react'

import {
  MOBILE_APP_CONTENT,
  type MobileAppVariant,
} from '@/app/config/mobileAppContent'
import { cn } from '@/app/utils/cn'
import { StoreQrCode } from '../StoreQrCode'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { useMobileAppModalController } from './useMobileAppModalController'

interface MobileAppModalProps {
  variant: MobileAppVariant
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function MobileAppModal({
  variant,
  open,
  onOpenChange,
}: MobileAppModalProps) {
  const content = MOBILE_APP_CONTENT[variant]
  const {
    dontShowAgain,
    setDontShowAgain,
    handleOpenChange,
    handleCopyCode,
    handleGenerateCode,
    generateCodeCta,
    appStoreUrl,
    displayUrl,
    learnMoreUrl,
    accessCodeDigits,
    hasCode,
    isGenerating,
    isError,
    isDesktop,
    downloadPageUrl,
  } = useMobileAppModalController({ variant, onOpenChange })

  // No mobile o link direto para a loja já funciona (o SO é o do próprio device).
  const storeLink = (
    <a
      href={appStoreUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'mt-2 inline-flex w-full items-center justify-between gap-2',
        'rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2',
        'text-xs text-emerald-700 hover:bg-emerald-100',
      )}
    >
      <span className="truncate">{displayUrl}</span>
      <ArrowUpRight className="h-3.5 w-3.5 flex-shrink-0" />
    </a>
  )

  // No desktop, oferecemos um QR Code para o usuário escanear com o celular.
  // Ele aponta para /baixar, que detecta iOS/Android e redireciona para a loja.
  const qrCode = (
    <StoreQrCode
      className="mt-2"
      value={downloadPageUrl}
      caption="Aponte a câmera do seu celular para o QR Code para baixar o app."
    />
  )

  const downloadStep = isDesktop ? qrCode : storeLink

  const generateCodeBlock = hasCode ? (
    <div className="mt-3">
      <div className="flex items-center justify-center gap-1.5 sm:gap-2">
        {accessCodeDigits.map((digit, idx) => (
          <Fragment key={idx}>
            {accessCodeDigits.length > 1 &&
              idx === Math.floor(accessCodeDigits.length / 2) && (
                <span className="text-lg font-bold text-gray-400 sm:text-2xl">
                  -
                </span>
              )}
            <div
              className={cn(
                'flex aspect-square w-full min-w-0 max-w-12 flex-1 items-center justify-center',
                'rounded-lg bg-gray-100',
                'text-lg font-bold text-gray-700 sm:text-2xl',
              )}
            >
              {digit}
            </div>
          </Fragment>
        ))}
      </div>
      <Button
        onClick={handleCopyCode}
        variant="ghost"
        className={cn(
          'mt-3 flex w-full items-center justify-center gap-2',
          'bg-emerald-50 font-semibold text-emerald-700 hover:bg-emerald-100 hover:text-emerald-700',
        )}
      >
        <Copy className="h-4 w-4" />
        Copiar código
      </Button>
    </div>
  ) : (
    <div className="mt-3">
      <Button
        onClick={handleGenerateCode}
        isLoading={isGenerating}
        className="w-full font-semibold text-white01"
      >
        {generateCodeCta}
      </Button>
      {isError && (
        <p className="mt-2 text-xs text-red-600">
          Não foi possível gerar o código. Tente novamente.
        </p>
      )}
    </div>
  )

  const steps = content.steps.map((step, idx) => ({
    number: idx + 1,
    title: step.title,
    description: step.description,
    extra: idx === 0 ? downloadStep : idx === 1 ? generateCodeBlock : undefined,
  }))

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        variant="bottomSheet"
        showCloseButton={true}
        className="max-h-[90vh] max-w-none gap-0 overflow-y-auto p-0 sm:max-w-md"
      >
        <DialogHeader className="px-6 pt-5">
          <DialogTitle className="text-left text-lg font-bold">
            {content.modalTitle}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 border-t border-gray-200" />

        <div className="px-6 pt-4">
          {steps.map((step, idx) => {
            const isLast = idx === steps.length - 1
            return (
              <div key={step.number} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      'flex h-7 w-7 items-center justify-center rounded-full',
                      'border-2 border-emerald-700 bg-white text-sm font-semibold text-emerald-700',
                    )}
                  >
                    {step.number}
                  </div>
                  {!isLast && (
                    <div className="my-1 w-0 flex-1 border-l-2 border-dashed border-gray-300" />
                  )}
                </div>
                <div className={cn('min-w-0 flex-1 pt-0.5', !isLast && 'pb-4')}>
                  <h3 className="text-sm font-semibold text-gray-900">
                    {step.title}
                  </h3>
                  <p className="mt-1 text-xs leading-snug text-gray-500">
                    {step.description}
                  </p>
                  {step.extra}
                </div>
              </div>
            )
          })}
        </div>

        {learnMoreUrl && (
          <>
            <div className="mt-4 border-t border-gray-200" />
            <div className="flex justify-center px-6 pt-4">
              <a
                href={learnMoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:text-emerald-800"
              >
                Saiba mais
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </>
        )}

        <div className="flex items-center justify-center gap-2 px-6 pb-4 pt-6">
          <Checkbox
            id="dont-show-again"
            checked={dontShowAgain}
            onCheckedChange={(checked) => setDontShowAgain(checked === true)}
            className="data-[state=checked]:border-emerald-700 data-[state=checked]:bg-emerald-700"
          />
          <label
            htmlFor="dont-show-again"
            className="cursor-pointer select-none text-sm text-gray-700"
          >
            Não exibir novamente
          </label>
        </div>
      </DialogContent>
    </Dialog>
  )
}
